import { Injectable } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Firestore, doc, docData, serverTimestamp, updateDoc } from '@angular/fire/firestore';
import { traceUntilFirst } from '@angular/fire/performance';
import { firstValueFrom, take } from 'rxjs';
import * as Sentry from '@sentry/angular';

import { AuthenticatedDataService } from 'src/app/shared/services/authenticated-data.service';
import { User, UserReference } from '../models/user.model';
import { UserIdService } from './user-id.service';
import { UserRolesService } from './user-roles.service';
import { PrimaryStateService } from './primary-state.service';
import { UserPhotoLookupService } from './user-photo-lookup.service';

@Injectable({
  providedIn: 'root'
})
export class CurrentUserService extends AuthenticatedDataService<User> {
  userId?: string;
  isAuthenticated: boolean = false;

  constructor(
    auth: Auth,
    userIdService: UserIdService,
    userRolesService: UserRolesService,
    private primaryStateService: PrimaryStateService,
    private firestore: Firestore,
    private userPhotoLookupService: UserPhotoLookupService,
  ) {
    super(auth, userIdService, userRolesService);
    this.refresh();
    this.init();
  }

  private loadSubscription: import('rxjs').Subscription | null = null;

  private async attachPhotoUrl(user: User): Promise<void> {
    if (!user.id || !user.photoPath) {
      return;
    }

    try {
      const photoUrls = await firstValueFrom(this.userPhotoLookupService.getPhotoUrls$([user.id]));
      const current = this.get();

      if (current?.id !== user.id || current.photoPath !== user.photoPath) {
        return;
      }

      await this.set({
        ...current,
        photoUrl: photoUrls.get(user.id) ?? undefined,
      });
    } catch (error) {
      console.error('Failed to resolve current user photo URL:', error);
    }
  }

  override async init() {
    const changeSubscription = this.userIdService
      .get$()
      .pipe(take(1))
      .subscribe(userId => this.load(userId));

    this.addSubscription(changeSubscription);
  }

  async load(userId?: string) {
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = null;

    this.userId = userId;
    this.isAuthenticated = !!userId;

    if (!userId) {
      return;
    }

    const dbReference = this.getDbReference();

    this.loadSubscription = dbReference.subscribe({
      next: res => {
        const user = { ...(res as User), photoUrl: undefined };
        this.set(user);
        void this.attachPhotoUrl(user);

        const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
        Sentry.setUser({
          id: user?.id,
        });

        if (user && user.primaryState) {
          this.primaryStateService.set(user.primaryState);
        }
      },
      error: err => {
        this.handleListenerError('CurrentUserService', err);
      },
    });

    this.addSubscription(this.loadSubscription);
  }

  getUserReference() {
    const user = this.get();
    const userRef = new UserReference();

    userRef.id = user?.id;
    userRef.email = user?.email;
    userRef.firstName = user?.firstName;
    userRef.lastName = user?.lastName;

    return userRef;
  }

  acceptTerms$() {
    if (!this.userId) {
      return;
    }

    const ref = doc(this.firestore, `users/${this.userId}`);

    updateDoc(ref, {
      acceptedTerms: true,
      acceptedTermsAt: serverTimestamp()
    });
  }

  private getDbReference() {
    const ref = doc(this.firestore, `users/${this.userId}`);

    return docData(ref, { idField: 'id' }).pipe(
      traceUntilFirst('firestore')
    );
  }
}
